import _ from 'lodash';
import { lsApiService, lsErrorService, lsFilters, lsImageHelpers } from 'ls-vue-common';
import {
  ONBOARDING__RESET,
  ONBOARDING__MUTATE_LOADING_STATUS,
  ONBOARDING__MUTATE_STEPS,
  ONBOARDING__MUTATE_LEADS_DETAILS,
  ONBOARDING__INITIALIZE,
  ONBOARDING__INITIALIZE_LEADS,
  ONBOARDING__GOTO_NEXT,
  ONBOARDING__UPDATE_PRICED_LTRS_SERVICE,
  ONBOARDING__UPDATE_QUOTE,
  ONBOARDING__REMOVE_SERVICE,
  ONBOARDING__CANCEL_ORDER,
  ONBOARDING__SYNC_ORDER,
  ONBOARDING__UPDATE_INSTANT_QUOTE,
  ONBOARDING__ACCEPT_INSTANT_QUOTE,
  ONBOARDING__REJECT_INSTANT_QUOTE,
  AUTH__PROCESSING_ORDER,
  AUTH__MUTATE_CUSTOMER,
  AUTH__SIGN_OUT,
  AUTH__TOKEN,
  ONBOARDING__STEPS,
  ONBOARDING__SELECTED_SERVICE,
  ONBOARDING__POST_ORDER_NEXT_SCREEN,
  FEATURE_FLAGS__ALL_FLAGS,
  FEATURE_FLAGS__REGISTER_EXPOSURE,
  ONBOARDING__LEADS_SUBMIT_CONTACTS,
  ONBOARDING__LEADS_SUBMIT_DETAILS,
  ONBOARDING__LEADS_GET_DETAILS,
  ONBOARDING__LEADS_SUBMIT_PREFERRED_QUOTE_TIMES,
  AUTH__PROCESSING_CUSTOMER_LEADS,
} from '@/store/types';
import { lsModuleHelpers } from '../helpers';
import router from '@/router';
import { serviceType } from '@/helpers';
import { lsOnboardingStoreHelpers } from './onboarding.helpers';
import { lsInstantQuoteStoreHelpers } from '../instant-quote/instant-quote.helpers';

const createInitialState = () => {
  return {
    loadingStatus: {
      isLoading: false,
      isUpdating: false,
      isRemoving: false,
    },
    // storing steps as paths so we can use this array as our single source of truth
    // in regards to comparing what route we're currently on and which is next

    steps: [],

    leadsDetails: {
      budgetOptions: [],
      stageOptions: [],
      preferredQuoteTimes: [],
    },
  };
};

export const mutations = {
  [ONBOARDING__RESET](state) {
    lsModuleHelpers.resetState({ state, createInitialState });
  },

  [ONBOARDING__MUTATE_LOADING_STATUS](state, payload) {
    _.assign(state.loadingStatus, payload);
  },

  [ONBOARDING__MUTATE_STEPS](state, payload) {
    state.steps = payload;
  },

  [ONBOARDING__MUTATE_LEADS_DETAILS](state, payload) {
    state.leadsDetails = payload;
  },
};

export const actions = {
  [ONBOARDING__INITIALIZE]({ commit, dispatch, state, rootGetters }) {
    dispatch(FEATURE_FLAGS__REGISTER_EXPOSURE, { flag: 'orders.customers.distribute_early' });
    dispatch(FEATURE_FLAGS__REGISTER_EXPOSURE, {
      flag: 'orders.customers.collect_bush_counts',
    });

    const processing_order = rootGetters[AUTH__PROCESSING_ORDER];

    if (_.isNull(processing_order.property_info.completed_at)) {
      const properyInfoView = [
        {
          path: '/onboarding/property-info',
        },
      ];

      commit(ONBOARDING__MUTATE_STEPS, _.concat(properyInfoView, state.steps));
    }

    if (!processing_order.policies_accepted_at) {
      const policiesAcceptedAt = [
        {
          path: '/onboarding/policies',
        },
      ];

      commit(ONBOARDING__MUTATE_STEPS, _.concat(state.steps, policiesAcceptedAt));
    }

    // default to true in case the initial FEATURE_FLAGS__GET_FLAGS call fails
    const distributeEarly = _.get(
      rootGetters[FEATURE_FLAGS__ALL_FLAGS],
      'orders.customers.distribute_early',
      true
    );

    if (distributeEarly && _.isNull(processing_order.distribute_early)) {
      const distributeEarlyView = [
        {
          path: '/onboarding/early',
        },
      ];
      commit(ONBOARDING__MUTATE_STEPS, _.concat(state.steps, distributeEarlyView));
    }

    const serviceSteps = lsOnboardingStoreHelpers.getAdditionalServiceRoutes({
      processing_order,
    });

    if (_.size(serviceSteps)) {
      commit(ONBOARDING__MUTATE_STEPS, _.concat(serviceSteps, state.steps));
    }

    if (!state.steps.length) {
      router.replace({ name: 'services' });
      return;
    }

    router.replace(state.steps[0]);
  },

  [ONBOARDING__INITIALIZE_LEADS]({ rootGetters, commit, dispatch, state }) {
    dispatch(ONBOARDING__LEADS_GET_DETAILS);

    let steps = [
      {
        path: '/onboarding-leads/choose-pros',
      },
      {
        path: '/onboarding-leads/success',
      },
      {
        path: '/onboarding-leads/share-contact-details',
      },
    ];

    const { customer_lead_contacts, stage, notes } = rootGetters[AUTH__PROCESSING_CUSTOMER_LEADS];

    // if any contact is in status:"pending_contact", we know the user is part of the "customer.signUp.landscapingLeads_V2" flow
    // and has completed the first two steps - so we replace the array with only the final details and share-contact-details steps
    if (_.some(customer_lead_contacts, { status: 'pending_contact' })) {
      steps = [
        {
          path: '/onboarding-leads/details',
        },
        {
          path: '/onboarding-leads/share-contact-details',
        },
      ];
    } else if (
      // else if stage and notes are null, we know the user made it through the "customer.signUp.landscapingLeads_V2" flow where signup no longer requests those
      // so we add the /details route just before share-contact-details
      _.isNull(stage) &&
      _.isNull(notes)
    ) {
      // after success step, before share-contact-details step
      steps.splice(2, 0, { path: '/onboarding-leads/details' });
    }

    commit(ONBOARDING__MUTATE_STEPS, steps);

    router.replace(state.steps[0]);
  },

  [ONBOARDING__LEADS_SUBMIT_CONTACTS]({ commit, rootState, rootGetters, dispatch }, { contacts }) {
    commit(ONBOARDING__MUTATE_LOADING_STATUS, {
      isUpdating: true,
    });

    const { customer } = rootState.auth.user;
    const { property_id, id } = rootGetters[AUTH__PROCESSING_CUSTOMER_LEADS];

    return lsApiService
      .request({
        method: 'POST',
        route: `/customers/${customer.id}/properties/${property_id}/customer-leads/${id}/contacts`,
        payload: { contacts },
      })
      .then(() => {
        dispatch(ONBOARDING__GOTO_NEXT);
      })
      .catch((error) => {
        lsErrorService.log({ error, showAlert: true });
      })
      .finally(() => {
        commit(ONBOARDING__MUTATE_LOADING_STATUS, {
          isUpdating: false,
        });
      });
  },

  [ONBOARDING__LEADS_SUBMIT_DETAILS](
    { commit, rootState, rootGetters, dispatch },
    { notes, budget, stage, images }
  ) {
    commit(ONBOARDING__MUTATE_LOADING_STATUS, {
      isUpdating: true,
    });

    const payload = new FormData();
    const imagesWithMeta = lsImageHelpers.generateImagesWithMetaPayload({
      images,
      tag: 'customer_leads.onboarding.landscaping',
      namePrefix: 'Customer Leads Onboarding Landscaping',
    });
    _.forEach(imagesWithMeta, (imageAndMeta, idx) => {
      const { image, meta } = imageAndMeta;
      payload.append(`images[${idx}]`, image);
      payload.append(`images_meta[${idx}][tag]`, meta.tag);
      payload.append(`images_meta[${idx}][title]`, meta.title);
    });

    payload.append('notes', notes);

    if (!_.isNull(budget.budget_low) && !_.isNull(budget.budget_high)) {
      payload.append('budget_low', budget.budget_low);
      payload.append('budget_high', budget.budget_high);
    }

    payload.append('stage', stage);

    const { customer } = rootState.auth.user;
    const { property_id, id } = rootGetters[AUTH__PROCESSING_CUSTOMER_LEADS];

    return lsApiService
      .request({
        method: 'POST',
        route: `/customers/${customer.id}/properties/${property_id}/customer-leads/${id}/details`,
        payload,
      })
      .then(() => {
        dispatch(ONBOARDING__GOTO_NEXT);
      })
      .catch((error) => {
        lsErrorService.log({ error, showAlert: true });
      })
      .finally(() => {
        commit(ONBOARDING__MUTATE_LOADING_STATUS, {
          isUpdating: false,
        });
      });
  },

  [ONBOARDING__LEADS_SUBMIT_PREFERRED_QUOTE_TIMES]({ commit, rootState, rootGetters }, { dates }) {
    commit(ONBOARDING__MUTATE_LOADING_STATUS, {
      isUpdating: true,
    });

    const { customer } = rootState.auth.user;
    const { property_id, id } = rootGetters[AUTH__PROCESSING_CUSTOMER_LEADS];

    return lsApiService
      .request({
        method: 'POST',
        route: `/customers/${
          customer.id
        }/properties/${property_id}/customer-leads/${id}/preferred-quote-times`,
        payload: { dates },
      })
      .catch((error) => {
        lsErrorService.log({ error });
      })
      .finally(() => {
        commit(ONBOARDING__MUTATE_LOADING_STATUS, {
          isUpdating: false,
        });
      });
  },

  [ONBOARDING__LEADS_GET_DETAILS]({ commit, rootState, rootGetters }) {
    commit(ONBOARDING__MUTATE_LOADING_STATUS, {
      isLoading: true,
    });

    const { customer } = rootState.auth.user;
    const { property_id, id } = rootGetters[AUTH__PROCESSING_CUSTOMER_LEADS];

    return lsApiService
      .request({
        route: `/customers/${customer.id}/properties/${property_id}/customer-leads/${id}/details`,
      })
      .then(({ data }) => {
        const leadsDetails = {};
        leadsDetails.budgetOptions = _.map(
          data.budgets,
          // eslint-disable-next-line camelcase
          ({ budget_low, budget_high }, idx) => {
            const low = _.replace(budget_low, /\B(?=(\d{3})+(?!\d))/g, ',');
            const high = _.replace(budget_high, /\B(?=(\d{3})+(?!\d))/g, ',');
            let label = `$${low} – $${high}`;

            if (idx === 0) {
              label = `Up to $${high}`;
            }

            return {
              label,
              value: { budget_low, budget_high },
            };
          }
        );

        leadsDetails.budgetOptions.push({
          label: 'Prefer not to say',
          value: {
            budget_low: null,
            budget_high: null,
          },
        });

        leadsDetails.stageOptions = _.map(data.stages, ({ display, value }) => ({
          label: display,
          value,
        }));

        leadsDetails.preferredQuoteTimes = data.preferred_quote_times;

        commit(ONBOARDING__MUTATE_LEADS_DETAILS, leadsDetails);
      })
      .catch((error) => {
        lsErrorService.log({ error, showAlert: true });
      })
      .finally(() => {
        commit(ONBOARDING__MUTATE_LOADING_STATUS, {
          isLoading: false,
        });
      });
  },

  [ONBOARDING__GOTO_NEXT]({ commit, state }, { replace = false } = {}) {
    const { path } = router.history.current;

    const currentStepIdx = _.findIndex(state.steps, { path });

    if (replace) {
      router.replace(state.steps[currentStepIdx + 1]);

      const currentSteps = [...state.steps];
      currentSteps.splice(currentStepIdx, 1);
      commit(ONBOARDING__MUTATE_STEPS, currentSteps);
    } else {
      router.push(state.steps[currentStepIdx + 1]);
    }
  },

  [ONBOARDING__UPDATE_PRICED_LTRS_SERVICE](
    { commit, dispatch, rootGetters },
    { goToNextStep, start_date, selectedService }
  ) {
    commit(ONBOARDING__MUTATE_LOADING_STATUS, {
      isUpdating: true,
    });

    const { order_id } = rootGetters[AUTH__PROCESSING_ORDER];

    const payload = {
      ...selectedService,
      start_date,
      description: `Requesting service week of ${lsFilters.date({
        date: start_date,
      })}`,
    };

    return lsApiService
      .request({
        method: 'PUT',
        route: `/orders/${order_id}/services/${selectedService.orderservice_id}`,
        payload,
      })
      .then(({ data }) => {
        commit(AUTH__MUTATE_CUSTOMER, { processing_order: data });
        if (goToNextStep) {
          dispatch(ONBOARDING__GOTO_NEXT);
        }
      })
      .catch((error) => {
        lsErrorService.log({ error, showAlert: true });
      })
      .finally(() => {
        commit(ONBOARDING__MUTATE_LOADING_STATUS, {
          isUpdating: false,
        });
      });
  },

  [ONBOARDING__UPDATE_QUOTE](
    { commit, dispatch, rootGetters },
    { selectedService, formData, images }
  ) {
    commit(ONBOARDING__MUTATE_LOADING_STATUS, {
      isUpdating: true,
    });

    const { order_id } = rootGetters[AUTH__PROCESSING_ORDER];

    if (!_.isEmpty(images)) {
      lsApiService
        .request({
          method: 'POST',
          route: `/orders/${order_id}/quotes/${selectedService.orderquote_id}/images`,
          payload: lsOnboardingStoreHelpers.generateQuoteImagePayload({
            images,
          }),
        })
        .catch((error) => {
          lsErrorService.log({ error });
        });
    }

    const { service_id } = selectedService.service;

    const payload = {
      ...selectedService,
      // endpoint expects service_id on root of payload in order to associate quotable properly
      service_id,
      ...lsOnboardingStoreHelpers.formatQuotePayload({
        formData,
        service_id,
      }),
    };

    return lsApiService
      .request({
        method: 'PUT',
        route: `/orders/${order_id}/quotes/${selectedService.orderquote_id}`,
        payload,
      })
      .then(({ data }) => {
        commit(AUTH__MUTATE_CUSTOMER, { processing_order: data });
        dispatch(ONBOARDING__GOTO_NEXT);
      })
      .catch((error) => {
        lsErrorService.log({ error, showAlert: true });
      })
      .finally(() => {
        commit(ONBOARDING__MUTATE_LOADING_STATUS, {
          isUpdating: false,
        });
      });
  },

  [ONBOARDING__REMOVE_SERVICE](
    { commit, dispatch, rootGetters },
    { orderservice_id, orderquote_id, onSuccess }
  ) {
    commit(ONBOARDING__MUTATE_LOADING_STATUS, {
      isRemoving: true,
    });

    const { order_id, cart } = rootGetters[AUTH__PROCESSING_ORDER];

    const cancelServiceOrQuoteRoute = orderservice_id
      ? `services/${orderservice_id}`
      : `quotes/${orderquote_id}`;

    return lsApiService
      .request({
        method: 'DELETE',
        route: `/orders/${order_id}/${cancelServiceOrQuoteRoute}`,
      })
      .then(() => {
        commit(AUTH__MUTATE_CUSTOMER, {
          processing_order: {
            ...rootGetters[AUTH__PROCESSING_ORDER],
            cart: {
              quotes: _.reject(cart.quotes, (cartQuote) => {
                return cartQuote.orderquote_id === orderquote_id;
              }),
              // just pass through instant_quotes as we do not remove them here
              instant_quotes: cart.instant_quotes,
              services: _.reject(cart.services, (cartService) => {
                return cartService.orderservice_id === orderservice_id;
              }),
            },
          },
        });
        onSuccess();
        dispatch(ONBOARDING__GOTO_NEXT, { replace: true });
      })
      .catch((error) => {
        lsErrorService.log({ error, showAlert: true });
      })
      .finally(() => {
        commit(ONBOARDING__MUTATE_LOADING_STATUS, {
          isRemoving: false,
        });
      });
  },

  [ONBOARDING__CANCEL_ORDER]({ commit, dispatch, rootGetters }, { onSuccess }) {
    commit(ONBOARDING__MUTATE_LOADING_STATUS, {
      isRemoving: true,
    });

    const { order_id } = rootGetters[AUTH__PROCESSING_ORDER];

    return lsApiService
      .request({
        method: 'POST',
        route: `/orders/${order_id}/cancel`,
      })
      .then(() => {
        if (window.isLSCustAppWebView) {
          window.ReactNativeWebView.postMessage(
            JSON.stringify({
              status: 'deniedPolicies',
            })
          );
        } else {
          onSuccess();
          dispatch(AUTH__SIGN_OUT);
        }
      })
      .catch((error) => {
        lsErrorService.log({ error, showAlert: true });
      })
      .finally(() => {
        // if coming from RN, let the spinner spin until the app logs them out
        if (window.isLSCustAppWebView) {
          return;
        }
        commit(ONBOARDING__MUTATE_LOADING_STATUS, {
          isRemoving: false,
        });
      });
  },

  // called from property-info, accepting policies, distribute early and bush count
  [ONBOARDING__SYNC_ORDER](
    { commit, state, dispatch, rootGetters },
    { property_info = {}, policies_accepted_at = null, distribute_early = null }
  ) {
    commit(ONBOARDING__MUTATE_LOADING_STATUS, {
      isUpdating: true,
    });

    const { order_id } = rootGetters[AUTH__PROCESSING_ORDER];

    const payload = {
      ...rootGetters[AUTH__PROCESSING_ORDER],
      distribute_early,
      property_info: {
        ...rootGetters[AUTH__PROCESSING_ORDER].property_info,
        ...property_info,
      },
    };

    // If distribute_early is not passed in, try and get the value if it was passed in previously
    if (_.isNull(distribute_early)) {
      distribute_early = _.get(rootGetters[AUTH__PROCESSING_ORDER], 'distribute_early', null);
    }

    // If value is passed in, add it to the payload so it can be sent to API
    if (policies_accepted_at) {
      payload.policies_accepted_at = policies_accepted_at;
    } else {
      // If value is not passed in, policies_accepted_at should not be sent to API,
      // but we should try and get the value if it was passed in previously
      policies_accepted_at = _.get(
        rootGetters[AUTH__PROCESSING_ORDER],
        'policies_accepted_at',
        null
      );
    }

    // default to true in case the initial FEATURE_FLAGS__GET_FLAGS call fails
    const distributeEarly = _.get(
      rootGetters[FEATURE_FLAGS__ALL_FLAGS],
      'orders.customers.distribute_early',
      true
    );

    const isLastScreen =
      (policies_accepted_at && !distributeEarly) ||
      (policies_accepted_at && !_.isNull(distribute_early));

    return lsApiService
      .request({
        method: 'PUT',
        route: `/orders/${order_id}`,
        payload,
      })
      .then(({ data }) => {
        commit(AUTH__MUTATE_CUSTOMER, { processing_order: data });
        if (isLastScreen) {
          if (window.isLSCustAppWebView) {
            window.ReactNativeWebView.postMessage(
              JSON.stringify({
                authtoken: rootGetters[AUTH__TOKEN],
                status: 'onboardingComplete',
              })
            );
          } else {
            const isTreatment = rootGetters[AUTH__PROCESSING_ORDER].type === serviceType.TREATMENT;

            if (isTreatment) {
              router.push({ name: 'onboardingWelcomeShare' });
            } else {
              // TODO PE-24506
              // Note: workaround to send the user to the Bush count View
              const goBackCount = (_.size(state.steps) - 1) * -1;
              router.go(goBackCount);
              // Bush count view there's a redirect to welcomeShare instead of to GOTO_NEXT
            }
          }
        } else {
          dispatch(ONBOARDING__GOTO_NEXT);
        }
      })
      .catch((error) => {
        lsErrorService.log({ error, showAlert: true });
      })
      .finally(() => {
        // if it's the last screen and on RN, we keep the loading spinner while the app navigates
        if (isLastScreen && window.isLSCustAppWebView) {
          return;
        }
        commit(ONBOARDING__MUTATE_LOADING_STATUS, {
          isUpdating: false,
        });
      });
  },

  [ONBOARDING__UPDATE_INSTANT_QUOTE](
    { commit, rootGetters },
    { instantQuotableId, formData, type }
  ) {
    const payloadHelperDict = {
      instant_quote_bush_trimming: lsInstantQuoteStoreHelpers.formatQuotePayloadBushTrimming,
      instant_quote_leaf_removal: lsInstantQuoteStoreHelpers.formatQuotePayloadLeafRemoval,
    };

    const payload = payloadHelperDict[type]({ formData });

    commit(ONBOARDING__MUTATE_LOADING_STATUS, {
      isUpdating: true,
    });

    const processing_order = rootGetters[AUTH__PROCESSING_ORDER];

    return lsApiService
      .request({
        method: 'POST',
        route: `/orders/${processing_order.order_id}/${_.kebabCase(type)}s/${instantQuotableId}`,
        payload,
      })
      .then(({ data }) => {
        const idx = _.findIndex(processing_order.cart.instant_quotes, {
          instant_quotable_id: instantQuotableId,
        });

        processing_order.cart.instant_quotes[idx] = {
          ...processing_order.cart.instant_quotes[idx],
          ...data,
        };

        commit(AUTH__MUTATE_CUSTOMER, {
          processing_order,
        });
      })
      .catch((error) => {
        lsErrorService.log({ error, showAlert: true });
      })
      .finally(() => {
        commit(ONBOARDING__MUTATE_LOADING_STATUS, {
          isUpdating: false,
        });
      });
  },

  [ONBOARDING__ACCEPT_INSTANT_QUOTE]({ commit, dispatch, rootGetters }, { instantQuoteId }) {
    commit(ONBOARDING__MUTATE_LOADING_STATUS, {
      isUpdating: true,
    });

    const processing_order = rootGetters[AUTH__PROCESSING_ORDER];

    return lsApiService
      .request({
        method: 'POST',
        route: `/orders/${processing_order.order_id}/instant-quotes/${instantQuoteId}/accept`,
      })
      .then(({ data }) => {
        const idx = _.findIndex(processing_order.cart.instant_quotes, {
          id: instantQuoteId,
        });

        processing_order.cart.instant_quotes[idx] = {
          ...processing_order.cart.instant_quotes[idx],
          ...data,
        };

        commit(AUTH__MUTATE_CUSTOMER, {
          processing_order,
        });
        dispatch(ONBOARDING__GOTO_NEXT);
      })
      .catch((error) => {
        lsErrorService.log({ error, showAlert: true });
      })
      .finally(() => {
        commit(ONBOARDING__MUTATE_LOADING_STATUS, {
          isUpdating: false,
        });
      });
  },

  [ONBOARDING__REJECT_INSTANT_QUOTE](
    { commit, dispatch, rootGetters },
    { instantQuoteId, formData, onSuccess = _.noop() }
  ) {
    commit(ONBOARDING__MUTATE_LOADING_STATUS, {
      isRemoving: true,
    });

    const { order_id, cart } = rootGetters[AUTH__PROCESSING_ORDER];

    return lsApiService
      .request({
        method: 'POST',
        route: `/orders/${order_id}/instant-quotes/${instantQuoteId}/reject`,
        payload: {
          ...formData,
        },
      })
      .then(() => {
        commit(AUTH__MUTATE_CUSTOMER, {
          processing_order: {
            ...rootGetters[AUTH__PROCESSING_ORDER],
            cart: {
              instant_quotes: _.reject(cart.instant_quotes, (cartQuote) => {
                return cartQuote.id === instantQuoteId;
              }),
              // pass through services and quotes
              services: cart.services,
              quotes: cart.quotes,
            },
          },
        });

        onSuccess();
        dispatch(ONBOARDING__GOTO_NEXT, { replace: true });
      })
      .catch((error) => {
        lsErrorService.log({ error, showAlert: true });
      })
      .finally(() => {
        commit(ONBOARDING__MUTATE_LOADING_STATUS, {
          isRemoving: false,
        });
      });
  },
};

export const getters = {
  [ONBOARDING__STEPS](state) {
    return state.steps;
  },
  // id param will be str_id if coming from ltrs or service_id if coming from non-ltrs
  [ONBOARDING__SELECTED_SERVICE]: (state, getters, rootState, rootGetters) => (id) => {
    const processing_order = rootGetters[AUTH__PROCESSING_ORDER];

    return _.get(
      lsOnboardingStoreHelpers.sortByIDs({
        processing_order,
      }),
      id
    );
  },
  [ONBOARDING__POST_ORDER_NEXT_SCREEN]: (state, getters, rootState, rootGetters) => {
    const processing_order = rootGetters[AUTH__PROCESSING_ORDER];

    const hasBushCounts = _.sum([
      _.get(processing_order, 'property_info.bushes_0ft_5ft_count'),
      _.get(processing_order, 'property_info.bushes_5ft_10ft_count'),
      _.get(processing_order, 'property_info.bushes_over_10ft_count'),
    ]);

    if (
      // default to true in case the initial FEATURE_FLAGS__GET_FLAGS call fails
      _.get(
        rootGetters[FEATURE_FLAGS__ALL_FLAGS],
        ['orders.customers.collect_bush_counts'],
        true
      ) &&
      !hasBushCounts
    ) {
      return 'onboardingBushCountView';
    }
    return 'onboardingWelcomeShare';
  },
};

export const onboarding = {
  state: createInitialState,
  mutations,
  actions,
  getters,
};
